import { Box, useLayoutContext, useUploadImage } from "arkh";
import React, { useCallback } from "react";
import {
  useBottomSheet,
  useBottomSheetInternal,
  useBottomSheetModal,
} from "@gorhom/bottom-sheet";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
} from "react-native-reanimated";
import { OutlinedHeaderButton } from "../../RoutineOccasion/RoutineOccasionScreen/OutlinedHeaderButton";
import { RichDescriptionInputButton } from "../../../components/RichDescriptionInput";
import { MoreButton } from "../../../common/MoreButton";
import { useTodoEditMoreMenuItems } from "../../TodoEdit/TodoEditNewMain/useTodoEditMoreMenuItems";
import { useOpenCameraWithPromise } from "../../../components/mobile/useImageActionSheet";

import { Platform } from "react-native";
import { useTodoUploadImageLink } from "../../TodoEdit/TodoEditNewMain/todoMutationHooks/useTodoUploadImageLink";

import { graphql, useFragment } from "react-relay";
import { TodoBottomSheetMainHeaderRight_todo$key } from "./__generated__/TodoBottomSheetMainHeaderRight_todo.graphql";

export function TodoBottomSheetMainHeaderRight(props: {
  mode: "create" | "edit";
  todo: TodoBottomSheetMainHeaderRight_todo$key | null;
  openTodoMoveToCategory: (todoId: string) => void;
}) {
  const layout = useLayoutContext();
  const offset = layout.space.medium + layout.circle.medium;
  const bottomSheetInternal = useBottomSheetInternal();
  const bottomSheetModal = useBottomSheetModal();
  const { expand } = useBottomSheet();
  const todo = useFragment(
    graphql`
      fragment TodoBottomSheetMainHeaderRight_todo on Todo {
        id
        ...useTodoEditMoreMenuItems_todo
      }
    `,
    props.todo
  );
  const menuItems = useTodoEditMoreMenuItems({
    close: () => {
      bottomSheetModal.dismiss();
    },
    onMoveToCategory: props.openTodoMoveToCategory,

    todo: todo ?? null,
  });

  const animation = useDerivedValue(() =>
    interpolate(
      bottomSheetInternal.animatedIndex.value,
      [-1, 0, 0.2, 1],
      [0, 0, 1, 1]
    )
  );

  const cameraAnimatedStyle = useAnimatedStyle(() => {
    if (props.mode === "create") {
      return {
        opacity: interpolate(animation.value, [0, 1], [1, 0]),
        transform: [
          {
            translateX: interpolate(animation.value, [0, 1], [0, offset]),
          },
        ],
      };
    }
    return {};
  });

  const moreAnimatedStyle = useAnimatedStyle(() => {
    if (props.mode === "create") {
      return {
        opacity: interpolate(animation.value, [0, 1], [0, 1]),
        transform:
          Platform.OS === "web"
            ? []
            : [
                {
                  translateX: interpolate(
                    bottomSheetInternal.animatedIndex.value,
                    [0, 1],
                    [0, offset]
                  ),
                },
              ],
      };
    }
    return {};
  });

  const [prepareUploadLink] = useTodoUploadImageLink(todo?.id ?? "");

  const openCamera = useOpenCameraWithPromise({
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });

  const { prepareForImageUpload, uploadImage } = useUploadImage();
  const onCameraAction = useCallback(async () => {
    const prepareUploadLinkPromise = prepareUploadLink();
    const response = await openCamera();
    if (!response) {
      return;
    }
    const preparedUploadLink = await prepareUploadLinkPromise;
    expand();
    const uploadId = await prepareForImageUpload({
      width: response.width,
      height: response.height,
      localImageURI: response.uri,
      imageId: preparedUploadLink.imageId,
    });
    uploadImage({
      imageId: preparedUploadLink.imageId,
      uploadURL: preparedUploadLink.uploadURL,
      uploadId,
    });
  }, [
    expand,
    openCamera,
    prepareForImageUpload,
    prepareUploadLink,
    uploadImage,
  ]);

  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Animated.View style={moreAnimatedStyle}>
        {menuItems ? (
          <MoreButton items={menuItems}>
            {(onPress) => (
              <OutlinedHeaderButton
                icon="more"
                tintColor="text-primary"
                onPress={onPress}
              />
            )}
          </MoreButton>
        ) : null}
      </Animated.View>
      {Platform.OS !== "web" && props.mode === "create" ? (
        <>
          <Box spaceRight="medium" />
          <Animated.View style={[cameraAnimatedStyle]}>
            <RichDescriptionInputButton
              onPress={onCameraAction}
              type="camera"
              tintColor="text-inverted"
              backgroundColor="text-cta"
            />
          </Animated.View>
        </>
      ) : null}
    </Box>
  );
}
